import { createApi } from '@reduxjs/toolkit/query/react';

import { fetchBaseQuery } from './fetch-base-query';
import { userProfileApi } from './user-profile';

export interface AvailableOrganization {
  organizationID: number;
  organizationName: string;
}

export interface UserInfo {
  userOrgID: number;
  assignedRole: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  joinDate: number;
  status: boolean;
  isUser: boolean;
}

interface OrganizationData {
  currentOrgID: number;
  organizationName: string;
  userInfo: UserInfo[];
  orgJoinDate: number;
  packageName: string;
  packageDescription: string;
}

interface SelectOrganizationRequest {
  authUid: string;
  orgID: number;
}

interface SelectOrganizationResponse {
  Result: string;
  onlyOneOrg: boolean;
  selectedOrgName: string;
}

interface CreateOrganizationResponse {
  Result: string;
  orgID: number;
}

interface ModifyUserRequest {
  role: string;
  status: 'true' | 'false';
  id: number;
}

interface RoleChangeEmailRequest {
  role: string;
  status: 'true' | 'false';
  id: number;
}

export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  baseQuery: fetchBaseQuery(),
  endpoints: (builder) => ({
    /**
     * List of organizations the user has joined
     */
    getAvailableOrganizationData: builder.query<AvailableOrganization[], void>({
      query: () => ({
        url: '/organizationSelection',
        method: 'GET',
      }),
      transformResponse: (response: any) => response.organizationData,
    }),

    /**
     * Data regarding the currently selected organization.
     * Is used for the organization dashboard screen.
     */
    getOrgData: builder.query<OrganizationData, void>({
      query: () => ({
        url: '/organizationData',
        method: 'GET',
      }),
    }),

    /**
     * For sending a PUT request in order to change the selected organization.
     */
    selectOrganization: builder.mutation<
      SelectOrganizationResponse,
      SelectOrganizationRequest
    >({
      query: ({ orgID }) => {
        const formData = new FormData();
        formData.append('organizationID', orgID.toString());
        return {
          url: `/organizationSelection`,
          method: 'PUT',
          body: formData,
        };
      },
      async onQueryStarted(
        { authUid },
        { dispatch, getState, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          // Should really update getOrgData cache directly,
          // but this API does not return necessary data to do so.
          // So we trigger the actions manually.
          /**
           * Sample response:
           * Result: "Successfully Updated"
           * onlyOneOrg: false
           * selectedOrgName: "Org Name"
           */

          // Update userProfile directly to indicate that an org has been selected
          dispatch(
            userProfileApi.util.updateQueryData(
              'getProfile',
              authUid,
              (draft) => {
                draft.hasSelectedProfile = true;
              }
            )
          );
          await dispatch(
            organizationApi.endpoints.getOrgData.initiate(undefined, {
              forceRefetch: true,
            })
          );
        } catch (err) {
          // Error can be found in network tab and RTK middleware
        }
      },
    }),

    /**
     * Create a new organization
     * Used in the SelectOrganization component ('/organization-selection')
     */
    createNewOrganization: builder.mutation<CreateOrganizationResponse, string>(
      {
        query: (organizationName) => {
          const formData = new FormData();
          formData.append('organizationName', organizationName);
          return {
            url: `/newOrganization`,
            method: 'POST',
            body: formData,
          };
        },
        async onQueryStarted(orgName, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            /**
             * Sample response:
             * Result: "Successfully Created The Organization."
             * OrgID: org id
             */
            // Adding the new organization to the getAvailableOrganizationData cache
            // to trigger SelectOrganization to redirect to the new organization
            dispatch(
              organizationApi.util.upsertQueryData(
                'getAvailableOrganizationData',
                undefined,
                [{ organizationID: data.orgID, organizationName: orgName }]
              )
            );
          } catch (err) {
            // Error can be found in network tab and RTK middleware
          }
        },
      }
    ),

    /**
     * Send Email to inform user of role changes
     */
    roleChangeEmail: builder.mutation<void, RoleChangeEmailRequest>({
      query: (data) => {
        const formData = new FormData();
        formData.append('role', data.role);
        formData.append('status', data.status);
        formData.append('targetUserOrgPK', data.id.toString());
        return {
          url: '/roleChangeEmail',
          method: 'POST',
          body: formData,
        };
      },
    }),

    /**
     * For sending a PUT request in order to change a user's role
     * and status from the User Profile screen.
     */
    modifyUserData: builder.mutation<void, ModifyUserRequest>({
      query: (data) => {
        const formData = new FormData();
        formData.append('role', data.role);
        formData.append('status', data.status);
        formData.append('targetUserOrgPK', data.id.toString());
        return {
          url: '/manageUsers',
          method: 'PUT',
          body: formData,
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        /**
         * data returned is:
         *   "Result": "Succesfully Updated"
         */
        // Update cache in getOrgData
        dispatch(
          organizationApi.util.updateQueryData(
            'getOrgData',
            undefined,
            (draft) => {
              const user = draft.userInfo.find(
                ({ userOrgID }) => userOrgID === args.id
              );
              if (user) {
                user.assignedRole = args.role;
                user.status = args.status === 'true';
              }
            }
          )
        );
        await dispatch(
          organizationApi.endpoints.roleChangeEmail.initiate(args)
        );
      },
    }),
  }),
});
